import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut } from "lucide-react";
import LuminartLogo from "../components/LuminartLogo";
import ProjectsTab from "../components/admin/ProjectsTab";
import ServicesTab from "../components/admin/ServicesTab";
import SiteTextTab from "../components/admin/SiteTextTab";
import { adminApi, clearToken, formatApiError, getToken, setToken } from "../lib/adminApi";

const inputCls =
  "w-full bg-transparent border border-white/15 px-4 py-3.5 text-sm text-white placeholder:text-white/25 focus:outline-none focus:border-white/50 transition-colors";

const TABS = [
  { id: "projects", label: "Projects" },
  { id: "services", label: "Services" },
  { id: "text", label: "Site Text" },
];

const LoginForm = ({ onSignedIn }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await adminApi.post("/admin/login", { username, password });
      setToken(data.token);
      onSignedIn({ username: data.username, role: data.role });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center px-6" data-testid="admin-login">
      <div className="w-full max-w-sm border border-white/10 bg-[#0d0d10] p-8">
        <div className="flex items-center gap-3 mb-8">
          <LuminartLogo className="w-9 h-auto" glow={false} />
          <span className="font-heading text-sm tracking-[0.35em] uppercase text-white/80">
            Luminart
          </span>
        </div>
        <h1 className="font-heading text-xl font-light text-white">Owner sign in</h1>
        <p className="mt-2 text-sm text-white/40 font-light">
          Content management for luminartprojection.com
        </p>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <input
            data-testid="admin-username-input"
            className={inputCls}
            placeholder="Username"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <input
            data-testid="admin-password-input"
            type="password"
            className={inputCls}
            placeholder="Password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error && (
            <p data-testid="admin-login-error" className="text-xs text-red-400">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-button"
            className="w-full inline-flex items-center justify-center gap-2 bg-white text-black px-6 py-3.5 text-[11px] uppercase tracking-[0.25em] font-semibold hover:bg-[#c8b99d] transition-colors disabled:opacity-50"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {busy ? "Signing in…" : "Sign In"}
          </button>
        </form>
        <Link
          to="/"
          className="mt-6 inline-block text-[10px] uppercase tracking-[0.25em] text-white/30 hover:text-white/60 transition-colors"
        >
          ← Back to website
        </Link>
      </div>
    </div>
  );
};

export const AdminPage = () => {
  const [session, setSession] = useState(undefined); // undefined = checking
  const [tab, setTab] = useState("projects");

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!getToken()) {
      setSession(null);
      return;
    }
    adminApi
      .get("/admin/session")
      .then(({ data }) => setSession(data))
      .catch(() => {
        clearToken();
        setSession(null);
      });
  }, []);

  if (session === undefined) {
    return (
      <div className="min-h-screen bg-[#0a0a0c] flex items-center justify-center">
        <Loader2 size={22} className="animate-spin text-white/40" />
      </div>
    );
  }

  if (!session) {
    return <LoginForm onSignedIn={setSession} />;
  }

  const signOut = () => {
    clearToken();
    setSession(null);
    toast.success("Signed out.");
  };

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-white" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[#0a0a0c]/90 backdrop-blur-md">
        <div className="mx-auto max-w-5xl px-5 md:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LuminartLogo className="w-8 h-auto" glow={false} />
            <span className="text-[11px] uppercase tracking-[0.3em] text-white/70">
              Content Manager
            </span>
          </div>
          <button
            onClick={signOut}
            data-testid="admin-logout"
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-white/40 hover:text-white transition-colors"
          >
            <LogOut size={13} /> Sign out
          </button>
        </div>
        <div className="mx-auto max-w-5xl px-5 md:px-8 flex gap-1 pb-0">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`px-4 py-3 text-[11px] uppercase tracking-[0.25em] border-b-2 transition-colors ${
                tab === t.id
                  ? "border-[#c8b99d] text-white"
                  : "border-transparent text-white/40 hover:text-white/70"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-5 md:px-8 py-8 md:py-10 pb-24">
        {tab === "projects" && <ProjectsTab />}
        {tab === "services" && <ServicesTab />}
        {tab === "text" && <SiteTextTab />}
      </main>
    </div>
  );
};

export default AdminPage;
